/**
 * Care Team API Client
 * BP-06-03: Care Team & Goals Frontend
 */

import apiClient from '@/core/lib/api-client';
import type {
  CareTeamMember,
  CareTeamMemberType,
  CareTeamByCategory,
  CareTeamListResponse,
  CreateCareTeamMemberRequest,
  UpdateCareTeamMemberRequest,
  CareTeamFilters,
  CareTeamExportOptions,
  ProviderSearchParams,
  ProviderSearchResponse,
} from '@/features/care-team/types/careTeamTypes';

// Note: apiClient already has /api/v1 as base URL
const BASE_URL = '';

/**
 * Care Team API
 */
export const careTeamApi = {
  /**
   * Get all care team members for a family
   */
  getAll: async (
    familyId: string,
    filters?: CareTeamFilters
  ): Promise<CareTeamListResponse> => {
    const params = new URLSearchParams();
    
    if (filters?.category) params.append('category', filters.category);
    if (filters?.memberTypeId) params.append('member_type_id', filters.memberTypeId);
    if (filters?.childId) params.append('child_id', filters.childId);
    if (filters?.isActive !== undefined) params.append('is_active', String(filters.isActive));
    if (filters?.isPrimary !== undefined) params.append('is_primary', String(filters.isPrimary));
    if (filters?.search) params.append('search', filters.search);

    const queryString = params.toString();
    const url = `${BASE_URL}/families/${familyId}/care-team${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiClient.get<CareTeamListResponse>(url);
    return response.data;
  },

  /**
   * Get a single care team member by ID
   */
  getById: async (memberId: string): Promise<CareTeamMember> => {
    const response = await apiClient.get<CareTeamMember>(
      `${BASE_URL}/care-team/${memberId}`
    );
    return response.data;
  },

  /**
   * Get care team members for a specific child
   */
  getByChild: async (childId: string): Promise<CareTeamListResponse> => {
    const response = await apiClient.get<CareTeamListResponse>(
      `${BASE_URL}/children/${childId}/care-team`
    );
    return response.data;
  },

  /**
   * Get care team grouped by category
   */
  getByCategory: async (familyId: string): Promise<CareTeamByCategory[]> => {
    const response = await apiClient.get<CareTeamByCategory[]>(
      `${BASE_URL}/families/${familyId}/care-team/by-category`
    );
    return response.data;
  },

  /**
   * Create a new care team member
   */
  create: async (
    familyId: string,
    data: CreateCareTeamMemberRequest
  ): Promise<CareTeamMember> => {
    const response = await apiClient.post<CareTeamMember>(
      `${BASE_URL}/families/${familyId}/care-team`,
      data
    );
    return response.data;
  },

  /**
   * Update a care team member
   */
  update: async (
    memberId: string,
    data: UpdateCareTeamMemberRequest
  ): Promise<CareTeamMember> => {
    const response = await apiClient.patch<CareTeamMember>(
      `${BASE_URL}/care-team/${memberId}`,
      data
    );
    return response.data;
  },

  /**
   * Delete a care team member
   */
  delete: async (memberId: string): Promise<void> => {
    await apiClient.delete(`${BASE_URL}/care-team/${memberId}`);
  },

  /**
   * Set a care team member as primary for their type
   */
  setPrimary: async (memberId: string): Promise<CareTeamMember> => {
    const response = await apiClient.post<CareTeamMember>(
      `${BASE_URL}/care-team/${memberId}/set-primary`
    );
    return response.data;
  },

  /**
   * Get all available care team member types
   */
  getMemberTypes: async (): Promise<CareTeamMemberType[]> => {
    const response = await apiClient.get<CareTeamMemberType[]>(
      `${BASE_URL}/care-team-types`
    );
    return response.data;
  },

  /**
   * Export care team to CSV or PDF
   */
  export: async (
    familyId: string,
    options: CareTeamExportOptions
  ): Promise<Blob> => {
    const params = new URLSearchParams();
    params.append('format', options.format);
    if (options.includeInactive) params.append('include_inactive', 'true');
    if (options.childId) params.append('child_id', options.childId);

    const response = await apiClient.get(
      `${BASE_URL}/families/${familyId}/care-team/export?${params.toString()}`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  /**
   * Search provider directory
   */
  searchProviders: async (
    params: ProviderSearchParams
  ): Promise<ProviderSearchResponse> => {
    const searchParams = new URLSearchParams();
    
    if (params.query) searchParams.append('query', params.query);
    if (params.category) searchParams.append('category', params.category);
    if (params.memberTypeId) searchParams.append('member_type_id', params.memberTypeId);
    if (params.zipCode) searchParams.append('zip_code', params.zipCode);
    if (params.radius) searchParams.append('radius', String(params.radius));
    if (params.acceptsInsurance) searchParams.append('accepts_insurance', params.acceptsInsurance);
    if (params.page) searchParams.append('page', String(params.page));
    if (params.limit) searchParams.append('limit', String(params.limit));

    const response = await apiClient.get<ProviderSearchResponse>(
      `${BASE_URL}/provider-directory?${searchParams.toString()}`
    );
    return response.data;
  },
};

/**
 * Helper function to format care team member for display
 */
export function formatCareTeamMember(member: CareTeamMember): string {
  const parts = [member.name];
  if (member.title) parts.push(member.title);
  if (member.organization) parts.push(`at ${member.organization}`);
  return parts.join(', ');
}

/**
 * Helper function to get member initials
 */
export function getMemberInitials(name: string): string {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Helper function to get category color
 */
export function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    medical: 'sage',
    therapy: 'terracotta',
    education: 'navy',
    legal: 'navy-light',
    other: 'warm-gray',
  };
  return colors[category] || 'warm-gray';
}

